import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { PcRecommendedCallout } from '@/components/PcRecommendedCallout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useSession } from '@/hooks/useSession';
import { cn } from '@/lib/cn';
import { moduleTileClass } from '@/components/ui/inputStyles';

type ListEntriesResp = { ok: boolean; total?: number };
type InterfaceInfo = {
  ok: boolean;
  webhook_configured?: boolean;
  current_site_name?: string;
  current_site_code?: string;
};
type WechatStatus = {
  ok: boolean;
  wechat_auth_mode?: string;
  mp_secret_configured?: boolean;
  authorizer_appid?: string;
};
type ContentSettings = {
  ok: boolean;
  field_map?: Record<string, string>;
};

function CheckRow({
  done,
  title,
  detail,
  actionLabel,
  actionTo,
}: {
  done: boolean;
  title: string;
  detail: string;
  actionLabel: string;
  actionTo: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-2xl border px-4 py-3.5 shadow-sm transition-all sm:flex-row sm:items-center sm:justify-between',
        done
          ? 'border-emerald-200/90 bg-gradient-to-r from-emerald-50/95 to-teal-50/30 shadow-emerald-900/[0.04]'
          : 'border-slate-200/80 bg-gradient-to-b from-white to-slate-50/50 shadow-slate-900/[0.04]'
      )}
    >
      <div className="flex min-w-0 gap-3">
        <span
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold shadow-sm ring-1',
            done
              ? 'bg-gradient-to-b from-emerald-500 to-emerald-600 text-white ring-emerald-500/30'
              : 'bg-slate-100 text-slate-500 ring-slate-200/80'
          )}
          aria-hidden
        >
          {done ? <i className="fa-solid fa-check" /> : <i className="fa-solid fa-minus" />}
        </span>
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">{title}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-600">{detail}</p>
        </div>
      </div>
      <Link
        to={actionTo}
        className={cn(
          'inline-flex shrink-0 touch-manipulation items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition-all sm:ml-4',
          done
            ? 'border border-slate-200/90 bg-white text-slate-700 shadow-sm ring-1 ring-slate-900/[0.04] hover:border-slate-300 hover:bg-slate-50'
            : 'bg-gradient-to-b from-teal-500 to-teal-600 text-white shadow-md shadow-teal-900/20 ring-1 ring-teal-500/25 hover:from-teal-400 hover:to-teal-600'
        )}
      >
        {actionLabel}
      </Link>
    </div>
  );
}

export function DashboardPage() {
  const { currentSiteId, currentSiteName, currentSiteCode, username, needsOwnSite, capabilities } = useSession();
  const [loading, setLoading] = useState(true);
  const [totalEntries, setTotalEntries] = useState<number | null>(null);
  const [iface, setIface] = useState<InterfaceInfo | null>(null);
  const [wechat, setWechat] = useState<WechatStatus | null>(null);
  const [content, setContent] = useState<ContentSettings | null>(null);

  const load = useCallback(async () => {
    if (currentSiteId < 1) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [le, inf, wc, cs] = await Promise.all([
        apiFetch<ListEntriesResp>('list_entries', {
          method: 'GET',
          query: { page: 1, per_page: 1 },
        }),
        apiFetch<InterfaceInfo>('interface_info', { method: 'GET', query: { site_id: currentSiteId } }),
        apiFetch<WechatStatus>('get_wechat_bind_status', { method: 'GET', query: { site_id: currentSiteId } }),
        apiFetch<ContentSettings>('get_site_content_settings', { method: 'GET', query: { site_id: currentSiteId } }),
      ]);
      setTotalEntries(typeof le.total === 'number' ? le.total : 0);
      setIface(inf.ok ? inf : null);
      setWechat(wc.ok ? wc : null);
      setContent(cs.ok ? cs : null);
    } catch {
      setTotalEntries(null);
      setIface(null);
      setWechat(null);
      setContent(null);
    } finally {
      setLoading(false);
    }
  }, [currentSiteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const checklist = useMemo(() => {
    const whOk = iface?.webhook_configured === true;
    const mode = wechat?.wechat_auth_mode ?? 'component';
    const mpOk = wechat?.mp_secret_configured === true;
    const authOk =
      (wechat?.authorizer_appid && wechat.authorizer_appid.trim() !== '') || (mode === 'mp_secret' && mpOk);
    const fm = content?.field_map ?? {};
    const bodyOk = typeof fm.body === 'string' && fm.body.trim() !== '';
    const imgOk = typeof fm.images === 'string' && fm.images.trim() !== '';
    const mapOk = bodyOk && imgOk;

    return {
      whOk,
      authOk,
      mapOk,
      setupPct: [whOk, authOk, mapOk].filter(Boolean).length,
    };
  }, [iface, wechat, content]);

  if (currentSiteId < 1) {
    return (
      <div className="space-y-6">
        <PageHeader title="首页" description={needsOwnSite ? '请先创建您的第一个站点。' : '请先选择一个工作站点。'} />
        <Card title="提示">
          <p className="text-sm text-slate-600">
            {needsOwnSite
              ? '当前为普通用户账号，尚未创建站点。请前往「站点管理」新建站点后再进行配置与登记。'
              : '在「信息登记」等站点相关页面中切换工作站点后，即可查看登记数据与配置项。'}
          </p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return <Spinner label="加载工作台" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="工作台首页"
        description={`你好，${username || '用户'}。当前站点「${currentSiteName || '—'}」${
          currentSiteCode ? `（${currentSiteCode}）` : ''
        }。下方为常用入口、上线检查清单；找不到功能时请打开侧栏「帮助与功能地图」。`}
      />

      {capabilities.manage_users ? (
        <PcRecommendedCallout>
          您为<strong className="font-semibold text-amber-900">系统管理员</strong>
          ，侧栏中的「用户管理」「系统登录日志」仅对管理员显示；账号维护与安全审计建议在电脑浏览器中完成。普通登录用户不会看到这两项。
        </PcRecommendedCallout>
      ) : (
        <PcRecommendedCallout>
          站点配置、公众号授权与接入地址等步骤适合在电脑浏览器中完成；手机端可处理登记查看与简单操作。
        </PcRecommendedCallout>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-teal-200/60 bg-gradient-to-br from-teal-50/95 via-white to-teal-50/30 ring-1 ring-teal-500/10">
          <p className="text-xs font-semibold uppercase tracking-wide text-teal-800/90">登记条数</p>
          <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-slate-900">{totalEntries ?? '—'}</p>
          <p className="mt-2 text-xs leading-relaxed text-slate-600">当前站点金数据入库后的全部记录</p>
        </Card>
        <Card className="ring-1 ring-slate-200/60">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">配置进度</p>
          <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-slate-900">{checklist.setupPct}/3</p>
          <p className="mt-2 text-xs leading-relaxed text-slate-600">密钥、公众号、字段映射</p>
        </Card>
        <Card className="sm:col-span-2 lg:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">快捷操作</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link
              to="/entries"
              className="inline-flex items-center justify-center rounded-xl bg-gradient-to-b from-teal-500 to-teal-600 px-3 py-2 text-xs font-semibold text-white shadow-md shadow-teal-900/20 ring-1 ring-teal-500/25 transition hover:from-teal-400 hover:to-teal-600"
            >
              查看登记
            </Link>
            <Link
              to="/settings"
              className="inline-flex items-center justify-center rounded-xl border border-slate-200/90 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
            >
              字段与模板
            </Link>
            <Link
              to="/interfaces"
              className="inline-flex items-center justify-center rounded-xl border border-slate-200/90 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
            >
              接入与推送
            </Link>
            <Link
              to="/wechat"
              className="inline-flex items-center justify-center rounded-xl border border-slate-200/90 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
            >
              公众号授权
            </Link>
            <Link
              to="/miniprograms"
              className="inline-flex items-center justify-center rounded-xl border border-slate-200/90 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
            >
              小程序管理
            </Link>
            <Link
              to="/help"
              className="inline-flex items-center justify-center rounded-xl border border-slate-200/90 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
            >
              帮助地图
            </Link>
          </div>
        </Card>
      </div>

      <Card title="上线检查清单">
        <p className="mb-4 text-sm text-slate-600">
          按顺序完成下列三项后，金数据即可自动入库并尝试写入公众号草稿（仍需图片成功落盘等条件）。
        </p>
        <div className="space-y-3">
          <CheckRow
            done={checklist.whOk}
            title="① 站点对接密钥已配置"
            detail="在金数据填写带 key 的 POST 地址，或配置请求头密钥。"
            actionLabel={checklist.whOk ? '查看接入' : '去修改密钥'}
            actionTo={checklist.whOk ? '/interfaces' : '/sites'}
          />
          <CheckRow
            done={checklist.authOk}
            title="② 微信公众号已接入"
            detail="第三方平台扫码或 AppID+Secret 任选其一，用于写草稿。"
            actionLabel="公众号授权"
            actionTo="/wechat"
          />
          <CheckRow
            done={checklist.mapOk}
            title="③ 字段映射（正文 + 图片）"
            detail="将金数据 field_xx 映射到正文与多图，否则无法组稿。"
            actionLabel="内容与样式"
            actionTo="/settings"
          />
        </div>
      </Card>

      <Card title="模块导览（可点击）">
        <ul className="grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
          <li className={moduleTileClass}>
            <Link to="/entries" className="block rounded-lg outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-teal-500/80">
              <span className="font-semibold text-teal-800">信息登记</span>
              <span className="mt-0.5 block text-xs leading-relaxed text-slate-600">查询、编辑、推草稿、看原始 JSON</span>
            </Link>
          </li>
          <li className={moduleTileClass}>
            <Link to="/sites" className="block rounded-lg outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-teal-500/80">
              <span className="font-semibold text-teal-800">站点管理</span>
              <span className="mt-0.5 block text-xs leading-relaxed text-slate-600">多站点、对接密钥、Android 密钥</span>
            </Link>
          </li>
          <li className={moduleTileClass}>
            <Link to="/settings" className="block rounded-lg outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-teal-500/80">
              <span className="font-semibold text-teal-800">内容与样式</span>
              <span className="mt-0.5 block text-xs leading-relaxed text-slate-600">字段映射、HTML 模板、贴图/图文、AI 标题</span>
            </Link>
          </li>
          <li className={moduleTileClass}>
            <Link to="/miniprograms" className="block rounded-lg outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-teal-500/80">
              <span className="font-semibold text-teal-800">小程序管理</span>
              <span className="mt-0.5 block text-xs leading-relaxed text-slate-600">展示配置、站点转发策略与 Android 密钥</span>
            </Link>
          </li>
          <li className={moduleTileClass}>
            <Link to="/robot-management" className="block rounded-lg outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-teal-500/80">
              <span className="font-semibold text-teal-800">机器人管理</span>
              <span className="mt-0.5 block text-xs leading-relaxed text-slate-600">转发机器人编号、渠道与站点范围</span>
            </Link>
          </li>
          <li className={moduleTileClass}>
            <Link to="/interfaces" className="block rounded-lg outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-teal-500/80">
              <span className="font-semibold text-teal-800">接入与推送</span>
              <span className="mt-0.5 block text-xs leading-relaxed text-slate-600">数据流开关、金数据地址、AI、企微通知、诊断</span>
            </Link>
          </li>
          <li className={moduleTileClass}>
            <Link to="/log-center" className="block rounded-lg outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-teal-500/80">
              <span className="font-semibold text-teal-800">日志中心</span>
              <span className="mt-0.5 block text-xs leading-relaxed text-slate-600">Webhook 与草稿结果追溯</span>
            </Link>
          </li>
        </ul>
      </Card>
    </div>
  );
}
